/**
 * Utility functions for date formatting with GMT+7 timezone (Asia/Jakarta)
 */

// Safe parse function
export const parseDateKey = (dateString: string | Date | undefined | null): Date | null => {
  if (!dateString) return null;

  if (typeof dateString === 'string') {
    // If string ends with 'Z' or has timezone offset, use as is
    if (dateString.endsWith('Z') || dateString.match(/[+-]\d{2}:\d{2}$/)) {
      return new Date(dateString);
    }
    // Assume UTC for ISO strings without timezone (common from backend)
    // Append 'Z' to treat as UTC
    if (dateString.includes('T')) {
      return new Date(`${dateString}Z`);
    }
    return new Date(dateString);
  }
  return dateString;
}

/**
 * Format date to Indonesian locale with GMT+7 timezone
 */
export const formatDate = (dateString: string | Date | undefined | null, options?: Intl.DateTimeFormatOptions): string => {
  const date = parseDateKey(dateString);
  if (!date) return '-';


  // Default options for Indonesian locale
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Jakarta'
  };

  return date.toLocaleDateString('id-ID', { ...defaultOptions, ...options });
};

/**
 * Format date to short format (e.g., "15 Des 2024")
 */
/**
 * Format date to short format (e.g., "15 Des 2024")
 */
export const formatDateShort = (dateString: string | Date | undefined | null): string => {
  const date = parseDateKey(dateString);
  if (!date) return '-';

  return date.toLocaleDateString('id-ID', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'Asia/Jakarta'
  });
};

/**
 * Format date to full datetime string
 */
export const formatDateTime = (dateString: string | Date | undefined | null): string => {
  const date = parseDateKey(dateString);
  if (!date) return '-';

  return date.toLocaleString('id-ID', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'Asia/Jakarta'
  });
};

/**
 * Format relative time (e.g., "2 jam yang lalu")
 */
export const formatRelativeTime = (dateString: string | Date | undefined | null): string => {
  const date = parseDateKey(dateString);
  if (!date) return '-';

  const now = new Date();

  // Both date and now are Date objects with correct absolute time.
  // diff calculation is time-zone agnostic (timestamps are UTC).
  // We don't need to convert to GMT+7 string just to get diff.

  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'baru saja';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} menit yang lalu`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} jam yang lalu`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} hari yang lalu`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)} minggu yang lalu`;
  if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} bulan yang lalu`;

  return formatDateShort(date);
};

